import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterChips from "@/components/base/FilterChips";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ContentCard from "@/components/feature/ContentCard";
import { dramas } from "@/mocks/dramas";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { UnifiedContent } from "@/types/content";

const PAGE_SIZE = 4;

export default function DramaCategory() {
  useDocumentTitle({
    title: "我妻子的一切剧情介绍 - 剧集剧情分类",
    description:
      "《我妻子的一切》剧情分类页，提供主线剧情、支线解析与关键节点梳理，帮助观众系统了解剧集故事的完整脉络。",
    keywords: "我妻子的一切剧情,剧情介绍,剧集解析,分集剧情",
  });

  const [year, setYear] = useState("");
  const [tag, setTag] = useState("");
  const [page, setPage] = useState(1);

  const years = useMemo(
    () => Array.from(new Set(dramas.map((item) => item.year))).sort().reverse(),
    [],
  );
  const tags = useMemo(
    () => Array.from(new Set(dramas.flatMap((item) => item.tags))),
    [],
  );

  const filtered = useMemo(() => {
    return dramas.filter((item) => {
      const matchYear = !year || item.year === year;
      const matchTag = !tag || item.tags.includes(tag);
      return matchYear && matchTag;
    });
  }, [year, tag]);

  const unified: UnifiedContent[] = filtered.map((item) => ({
    id: item.id,
    category: "drama",
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    date: item.date,
    tags: item.tags,
  }));

  const totalPages = Math.max(1, Math.ceil(unified.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = unified.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setYear("");
    setTag("");
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "剧集剧情" }]} />

      <header className="mt-5 max-w-3xl">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Story Archive</p>
        <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          剧集剧情
        </h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
          《我妻子的一切》剧情板块收录主线故事、支线线索与关键节点解析。你可以按播出年份或剧情关键词筛选，
          快速定位到想深入了解的剧情段落。
        </p>
      </header>

      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <FilterChips label="年份" options={years} value={year} onChange={(v) => { setYear(v); setPage(1); }} allLabel="全部年份" />
        <FilterChips label="关键词" options={tags} value={tag} onChange={(v) => { setTag(v); setPage(1); }} allLabel="全部标签" />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共 <span className="font-medium text-primary-700">{unified.length}</span> 条剧情内容
        </p>
        {(year || tag) && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-5">
          <EmptyState
            icon="ri-book-2-line"
            title="暂无相关剧情内容"
            description="当前筛选条件下没有匹配的剧情文章，换个年份或关键词试试吧。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        </div>
      )}
    </div>
  );
}